import React, { useState } from 'react';
import { Plus, X, CheckSquare, Folder, FolderOpen, Calendar } from 'lucide-react';

interface FloatingActionButtonProps {
  onAddTask?: () => void;
  onAddDeliverable?: () => void;
  onAddMatter?: () => void;
  onAddProject?: () => void;
}

function FloatingActionButton({
  onAddTask,
  onAddDeliverable,
  onAddMatter,
  onAddProject
}: FloatingActionButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    { icon: Calendar, label: 'Task', onClick: onAddTask, color: 'bg-green-500 hover:bg-green-600' },
    { icon: CheckSquare, label: 'Deliverable', onClick: onAddDeliverable, color: 'bg-blue-500 hover:bg-blue-600' },
    { icon: Folder, label: 'Matter', onClick: onAddMatter, color: 'bg-purple-500 hover:bg-purple-600' },
    { icon: FolderOpen, label: 'Project', onClick: onAddProject, color: 'bg-indigo-500 hover:bg-indigo-600' },
  ].filter(action => action.onClick);

  if (actions.length === 0) return null;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB Container */}
      <div className="fixed bottom-20 right-4 z-50 md:hidden">
        {/* Action Buttons */}
        <div className={`absolute bottom-14 right-0 space-y-3 transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}>
          {actions.map((action, index) => (
            <div
              key={index}
              className="flex items-center justify-end space-x-2"
              style={{
                transitionDelay: isOpen ? `${index * 50}ms` : '0ms'
              }}
            >
              <span className="bg-gray-800 text-white text-sm px-3 py-1 rounded-full whitespace-nowrap">
                {action.label}
              </span>
              <button
                onClick={() => {
                  action.onClick?.();
                  setIsOpen(false);
                }}
                className={`w-12 h-12 rounded-full ${action.color} text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110`}
              >
                <action.icon className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        {/* Main FAB Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center transition-all duration-300 ${
            isOpen ? 'rotate-45' : ''
          }`}
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
    </>
  );
}

export default FloatingActionButton;